import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';

import { LOCAL_LANGUAGE } from '@/constants/storageKey';
import { HEADER_DROPDOWN_ZINDEX, HEADER_ZINDEX } from '@/constants/zindex';

import { Image } from '@/components/shared';
import { BoldText, RegularText } from '@/components/text';
import { IsDefault, IsMobile, mq } from '@/utils/mediaQuery';
import { LANGUAGE_LIST } from '@/utils/languages/languageOptions';

const Container = styled.div(() => ({}));

const Wrapper = styled.div(({ theme }) => ({
  position: 'relative',
  zIndex: HEADER_ZINDEX,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  padding: '2rem 1.6rem',
  background: theme.color.base_white,
  borderBottom: `2px solid ${theme.color.base_purple}`,

  [mq('desktop')]: {
    padding: '2rem 12rem',
  },
}));

const LanguageWrapper = styled.div(() => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  cursor: 'pointer',
}));

const ModalContainer = styled.div(({ theme, isShow }) => ({
  display: !isShow ? 'none' : '',
  position: 'absolute',
  top: 20,
  right: 0,
  zIndex: HEADER_DROPDOWN_ZINDEX,
  width: '16.4rem',

  background: theme.color.base_white,
  wordBreak: 'keep-all',
  borderRadius: '1rem',
  border: '1px solid #EEE',
  boxShadow:
    '0 0 0 0 rgba(0, 0, 0, 0.03), 0 8px 18px 0 rgba(0, 0, 0, 0.03), 0 33px 33px 0 rgba(0, 0, 0, 0.03), 0 75px 45px 0 rgba(0, 0, 0, 0.02)',
}));

const ModalTitle = styled.div(({ theme }) => ({
  display: 'none',
  textAlign: 'left',
  color: 'black',
  fontSize: '1.2rem',
  fontWeight: 700,
  letterSpacing: '0.01rem',
  textTransform: 'uppercase',
  padding: '1.6rem 0 0.8rem',
  margin: '0 1.6rem',
  borderBottom: '1px solid #D9D9D9',
  wordSpacing: '999em',
  lineHeight: '1.5',
  cursor: 'default',

  [mq('desktop')]: {
    display: 'block',
  },
}));

const ModalLanguageList = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '0.8rem 0',
}));

const ModalLanguageItem = styled.div(({ theme }) => ({
  width: '100%',
  padding: '0.8rem 1.6rem',

  ':hover': {
    background: theme.color.base_pink,
  },
}));

const Header = () => {
  const location = useLocation();

  const language = localStorage.getItem(LOCAL_LANGUAGE);

  const theme = useTheme();

  const modalRef = useRef();

  const [languageModal, setLanguageModal] = useState(false);

  const clickLanguageBtn = useCallback(() => {
    setLanguageModal((prev) => !prev);
  }, []);

  const handleLanguageSelect = useCallback(
    (code) => {
      const pathParts = location.pathname.split('/');

      pathParts[1] = code;

      const newPathname = pathParts.join('/');

      localStorage.setItem(LOCAL_LANGUAGE, code);

      window.location.href = `${window.location.origin}${newPathname}${window.location.search}`;
    },
    [location.pathname]
  );
  const handleClickOutside = useCallback((event) => {
    if (
      modalRef.current &&
      !modalRef.current.contains(event.target) &&
      !event.target.closest('.ModalLanguageItem')
    ) {
      setLanguageModal(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const currentLanguage = localStorage.getItem(LOCAL_LANGUAGE)
    ? localStorage.getItem(LOCAL_LANGUAGE)
    : 'en';

  return (
    <Container>
      <Wrapper>
        <IsDefault>
          <a href={`https://inspireresorts.com/${language}`} target="_blank" rel="noreferrer">
            <Image src={theme.image.logoHeader} alt="logoHeader" width="auto" height="3rem" />
          </a>
        </IsDefault>

        <IsMobile>
          <a href={`https://inspireresorts.com/${language}`} target="_blank" rel="noreferrer">
            <Image src={theme.image.logoHeader} alt="logoHeader" width="auto" height="2rem" />
          </a>
        </IsMobile>

        <LanguageWrapper onClick={clickLanguageBtn} onMouseDown={(e) => e.stopPropagation()}>
          <RegularText fontSize="1.6rem">
            {currentLanguage &&
              LANGUAGE_LIST?.filter((el) => el.language === localStorage.getItem(LOCAL_LANGUAGE))[0]
                ?.name}
          </RegularText>

          <Image src={theme.image.arrowDown} alt="arrowDown" width="1.5rem" height="1.5rem" />

          <ModalContainer ref={modalRef} isShow={languageModal}>
            <ModalTitle>
              <BoldText id="lang-switch-language" fontSize="1.2rem" />
            </ModalTitle>

            <ModalLanguageList>
              {LANGUAGE_LIST?.map((language, index) => {
                if (language.language !== currentLanguage) {
                  return (
                    <ModalLanguageItem
                      key={index}
                      onClick={() => handleLanguageSelect(language.language)}
                    >
                      <RegularText fontSize="1.6rem">
                        <p>{language.name}</p>
                      </RegularText>
                    </ModalLanguageItem>
                  );
                }

                return null;
              })}
            </ModalLanguageList>
          </ModalContainer>
        </LanguageWrapper>
      </Wrapper>
    </Container>
  );
};

export default Header;
